// @ts-nocheck

import { dispatchWishlistRefresh } from '@theme/events';
import ThemeToast from '@theme/lib-toast';
import { getSideCartBehavior } from '@theme/lib-cart';

const QUICK_VIEW_SECTION_ID = 'quick-view-product';
/** @type {Set<string>} */
const inFlightDialogIds = new Set();

/**
 * @param {string} dialogId
 * @returns {HTMLElement | null}
 */
const getDialogContentById = (dialogId) => {
  const dialogContents = document.querySelectorAll('dialog-content');

  for (const dialogContent of dialogContents) {
    if (dialogContent instanceof HTMLElement && dialogContent.id === dialogId) {
      return dialogContent;
    }
  }

  return null;
};

/**
 * @returns {HTMLElement}
 */
function getQuickViewPortal() {
  let portal = document.getElementById('quick-view-portal');

  if (!(portal instanceof HTMLElement)) {
    portal = document.createElement('div');
    portal.id = 'quick-view-portal';
    portal.hidden = true;
    document.body.appendChild(portal);
  }

  return portal;
}

/**
 * @param {HTMLElement} trigger
 * @returns {{ productId: string, productUrl: string, dialogId: string } | null}
 */
function getQuickViewContext(trigger) {
  const productId = trigger.dataset.productId;
  const productUrl = trigger.dataset.productUrl;

  if (productId && productUrl) {
    return {
      productId,
      productUrl,
      dialogId: `quick-view-${productId}`,
    };
  }

  const dialogTarget = trigger.dataset.dialogTarget;
  if (dialogTarget && dialogTarget.startsWith('quick-view-')) {
    const resolvedId = dialogTarget.replace('quick-view-', '');
    const button = trigger.querySelector('[data-product-url]');
    const resolvedUrl =
      trigger.dataset.productUrl ||
      (button instanceof HTMLElement ? button.dataset.productUrl : '');

    if (resolvedId && resolvedUrl) {
      return {
        productId: resolvedId,
        productUrl: resolvedUrl,
        dialogId: dialogTarget,
      };
    }
  }

  return null;
}

/**
 * @param {string} html
 * @returns {HTMLElement | null}
 */
function extractDialogContent(html) {
  if (!html || typeof html !== 'string') return null;

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const dialogContent = parsed.querySelector('dialog-content');

  return dialogContent instanceof HTMLElement ? dialogContent : null;
}

/**
 * @param {string} productUrl
 * @returns {Promise<string>}
 */
async function fetchQuickViewSectionMarkup(productUrl) {
  const resolvedProductUrl = new URL(productUrl, window.location.origin);

  const sectionIdUrl = new URL(resolvedProductUrl.toString());
  sectionIdUrl.searchParams.set('section_id', QUICK_VIEW_SECTION_ID);

  const sectionIdResponse = await fetch(sectionIdUrl.toString(), {
    credentials: 'same-origin',
    headers: { Accept: 'text/html' },
  });

  if (sectionIdResponse.ok) {
    const sectionHtml = await sectionIdResponse.text();
    if (extractDialogContent(sectionHtml)) {
      return sectionHtml;
    }
  }

  const sectionsUrl = new URL(resolvedProductUrl.toString());
  sectionsUrl.searchParams.set('sections', QUICK_VIEW_SECTION_ID);

  const sectionsResponse = await fetch(sectionsUrl.toString(), {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!sectionsResponse.ok) {
    throw new Error('Failed to load quick view');
  }

  const payload = await sectionsResponse.json();
  const sectionMarkup =
    payload?.[QUICK_VIEW_SECTION_ID] ||
    payload?.sections?.[QUICK_VIEW_SECTION_ID];

  if (!sectionMarkup || typeof sectionMarkup !== 'string') {
    throw new Error('Quick view section markup missing');
  }

  return sectionMarkup;
}

/**
 * @param {string} dialogId
 * @param {string} productUrl
 * @returns {Promise<string>}
 */
async function ensureQuickViewDialog(dialogId, productUrl) {
  if (getDialogContentById(dialogId)) {
    return dialogId;
  }

  if (inFlightDialogIds.has(dialogId)) {
    await new Promise((resolve) => {
      const interval = window.setInterval(() => {
        if (
          !inFlightDialogIds.has(dialogId) ||
          getDialogContentById(dialogId)
        ) {
          window.clearInterval(interval);
          resolve();
        }
      }, 50);
    });
    return dialogId;
  }

  inFlightDialogIds.add(dialogId);

  try {
    const sectionMarkup = await fetchQuickViewSectionMarkup(productUrl);
    const dialogContent = extractDialogContent(sectionMarkup);

    if (!dialogContent) {
      throw new Error('Quick view dialog content missing');
    }

    getQuickViewPortal().appendChild(document.importNode(dialogContent, true));
    dispatchWishlistRefresh();

    return dialogId;
  } finally {
    inFlightDialogIds.delete(dialogId);
  }
}

/**
 * @param {HTMLElement} trigger
 */
async function openQuickView(trigger) {
  const context = getQuickViewContext(trigger);
  if (!context) return;

  const interactive = trigger.matches('button, [data-quick-view-trigger]')
    ? trigger
    : trigger.querySelector('button, [data-quick-view-trigger]');

  if (interactive instanceof HTMLElement) {
    interactive.setAttribute('aria-busy', 'true');
  }

  try {
    await ensureQuickViewDialog(context.dialogId, context.productUrl);
    window.dialog?.open(context.dialogId);
  } catch (error) {
    const message =
      error instanceof Error && error.message
        ? error.message
        : 'Unable to open quick view';

    ThemeToast.error(message, {
      position: getSideCartBehavior().toastPosition,
    });
  } finally {
    if (interactive instanceof HTMLElement) {
      interactive.removeAttribute('aria-busy');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.body.addEventListener(
    'click',
    /** @param {MouseEvent} event */
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const trigger = target.closest(
        '[data-quick-view-trigger], .quick-view-button, dialog-trigger[data-dialog-target^="quick-view-"]'
      );

      if (!(trigger instanceof HTMLElement)) return;

      const context = getQuickViewContext(trigger);
      if (!context) return;

      if (getDialogContentById(context.dialogId)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      openQuickView(trigger);
    },
    true
  );
});
